import * as React from "react";
import { useMemo } from "react";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { jsxDEV } from "react/jsx-dev-runtime";
import { AlertDialog as AlertDialog$1 } from "@base-ui/react/alert-dialog";
import { Button as Button$1 } from "@base-ui/react/button";
import { Avatar as Avatar$1 } from "@base-ui/react/avatar";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { Separator as Separator$1 } from "@base-ui/react/separator";
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, CircleCheckIcon, InfoIcon, Loader2Icon, MinusIcon, OctagonXIcon, PanelLeftIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { Input as Input$1 } from "@base-ui/react/input";
import { Combobox as Combobox$1 } from "@base-ui/react";
import { Dialog as Dialog$1 } from "@base-ui/react/dialog";
import { Menu } from "@base-ui/react/menu";
import { Controller, FormProvider, useFormContext, useFormState } from "react-hook-form";
import { OTPInput, OTPInputContext } from "input-otp";
import { Select as Select$1 } from "@base-ui/react/select";
import { Tooltip } from "@base-ui/react/tooltip";
import { useTheme } from "next-themes";
import { Toaster as Toaster$1, toast } from "sonner";
//#region src/lib/utils.ts
function cn(...inputs) {
	return twMerge(clsx(inputs));
}
//#endregion
//#region src/components/ui/alert.tsx
var _jsxFileName$23 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/alert.tsx";
var alertVariants = cva("grid gap-0.5 rounded-lg border px-2 py-1.5 text-left text-xs/relaxed has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-1.5 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-3.5 w-full relative group/alert", {
	variants: { variant: {
		default: "bg-card text-card-foreground",
		destructive: "text-destructive bg-card *:data-[slot=alert-description]:text-destructive/90 *:[svg]:text-current"
	} },
	defaultVariants: { variant: "default" }
});
function Alert({ className, variant, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "alert",
		role: "alert",
		className: cn(alertVariants({ variant }), className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$23,
		lineNumber: 24,
		columnNumber: 5
	}, this);
}
function AlertTitle({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "alert-title",
		className: cn("font-medium group-has-[>svg]/alert:col-start-2 [&_a]:hover:text-foreground [&_a]:underline [&_a]:underline-offset-3", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$23,
		lineNumber: 35,
		columnNumber: 5
	}, this);
}
function AlertDescription({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "alert-description",
		className: cn("text-muted-foreground text-xs/relaxed text-balance md:text-pretty [&_p:not(:last-child)]:mb-4 [&_a]:hover:text-foreground [&_a]:underline [&_a]:underline-offset-3", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$23,
		lineNumber: 51,
		columnNumber: 5
	}, this);
}
function AlertAction({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "alert-action",
		className: cn("absolute top-1.5 right-2", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$23,
		lineNumber: 64,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/button.tsx
var _jsxFileName$22 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/button.tsx";
var buttonVariants = cva("focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-md border border-transparent bg-clip-padding text-xs/relaxed font-medium focus-visible:ring-[2px] aria-invalid:ring-[2px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none", {
	variants: {
		variant: {
			default: "bg-primary text-primary-foreground hover:bg-primary/80",
			outline: "border-border dark:bg-input/30 hover:bg-input/50 hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
			secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
			glass: "border-primary-foreground/46 bg-primary-foreground/14 text-primary-foreground shadow-[0_12px_28px_-24px_rgb(0_0_0_/_0.6)] [text-shadow:0_1px_2px_rgb(0_0_0_/_0.2)] backdrop-blur-md hover:bg-primary-foreground/26 group-data-[solid=true]/toolbar:border-foreground/15 group-data-[solid=true]/toolbar:bg-foreground/6 group-data-[solid=true]/toolbar:text-foreground/92 group-data-[solid=true]/toolbar:[text-shadow:none] group-data-[solid=true]/toolbar:hover:bg-foreground/12",
			ghost: "hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground",
			destructive: "bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
			link: "text-primary underline-offset-4 hover:underline"
		},
		size: {
			default: "h-7 gap-1 px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
			xs: "h-5 gap-1 rounded-sm px-2 text-[0.625rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-2.5",
			sm: "h-6 gap-1 px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
			lg: "h-8 gap-1 px-2.5 text-xs/relaxed has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-4",
			icon: "size-7 [&_svg:not([class*='size-'])]:size-3.5",
			"icon-xs": "size-5 rounded-sm [&_svg:not([class*='size-'])]:size-2.5",
			"icon-sm": "size-6 [&_svg:not([class*='size-'])]:size-3",
			"icon-lg": "size-8 [&_svg:not([class*='size-'])]:size-4"
		}
	},
	defaultVariants: {
		variant: "default",
		size: "default"
	}
});
function Button({ className, variant = "default", size = "default", ...props }) {
	return /* @__PURE__ */ jsxDEV(Button$1, {
		"data-slot": "button",
		className: cn(buttonVariants({
			variant,
			size,
			className
		})),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$22,
		lineNumber: 50,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/alert-dialog.tsx
var _jsxFileName$21 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/alert-dialog.tsx";
function AlertDialog({ ...props }) {
	return /* @__PURE__ */ jsxDEV(AlertDialog$1.Root, {
		"data-slot": "alert-dialog",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 8,
		columnNumber: 10
	}, this);
}
function AlertDialogTrigger({ ...props }) {
	return /* @__PURE__ */ jsxDEV(AlertDialog$1.Trigger, {
		"data-slot": "alert-dialog-trigger",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 13,
		columnNumber: 5
	}, this);
}
function AlertDialogPortal({ ...props }) {
	return /* @__PURE__ */ jsxDEV(AlertDialog$1.Portal, {
		"data-slot": "alert-dialog-portal",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 19,
		columnNumber: 5
	}, this);
}
function AlertDialogOverlay({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(AlertDialog$1.Backdrop, {
		"data-slot": "alert-dialog-overlay",
		className: cn("data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/80 duration-100 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 isolate z-50", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 28,
		columnNumber: 5
	}, this);
}
function AlertDialogContent({ className, size = "default", ...props }) {
	return /* @__PURE__ */ jsxDEV(AlertDialogPortal, { children: [/* @__PURE__ */ jsxDEV(AlertDialogOverlay, {}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 48,
		columnNumber: 7
	}, this), /* @__PURE__ */ jsxDEV(AlertDialog$1.Popup, {
		"data-slot": "alert-dialog-content",
		"data-size": size,
		className: cn("data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 bg-background ring-foreground/10 gap-3 rounded-xl p-4 ring-1 duration-100 data-[size=default]:max-w-xs data-[size=sm]:max-w-64 data-[size=default]:sm:max-w-sm group/alert-dialog-content fixed top-1/2 left-1/2 z-50 grid w-full -translate-x-1/2 -translate-y-1/2 outline-none", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 49,
		columnNumber: 7
	}, this)] }, void 0, true, {
		fileName: _jsxFileName$21,
		lineNumber: 47,
		columnNumber: 5
	}, this);
}
function AlertDialogHeader({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "alert-dialog-header",
		className: cn("grid grid-rows-[auto_1fr] place-items-center gap-1 text-center has-data-[slot=alert-dialog-media]:grid-rows-[auto_auto_1fr] has-data-[slot=alert-dialog-media]:gap-x-4 sm:group-data-[size=default]/alert-dialog-content:place-items-start sm:group-data-[size=default]/alert-dialog-content:text-left sm:group-data-[size=default]/alert-dialog-content:has-data-[slot=alert-dialog-media]:grid-rows-[auto_1fr]", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 67,
		columnNumber: 5
	}, this);
}
function AlertDialogFooter({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "alert-dialog-footer",
		className: cn("flex flex-col-reverse gap-2 group-data-[size=sm]/alert-dialog-content:grid group-data-[size=sm]/alert-dialog-content:grid-cols-2 sm:flex-row sm:justify-end", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 80,
		columnNumber: 5
	}, this);
}
function AlertDialogMedia({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "alert-dialog-media",
		className: cn("bg-muted mb-2 inline-flex size-8 items-center justify-center rounded-md sm:group-data-[size=default]/alert-dialog-content:row-span-2 *:[svg:not([class*='size-'])]:size-4", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 96,
		columnNumber: 5
	}, this);
}
function AlertDialogTitle({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(AlertDialog$1.Title, {
		"data-slot": "alert-dialog-title",
		className: cn("text-sm font-medium sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 109,
		columnNumber: 5
	}, this);
}
function AlertDialogDescription({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(AlertDialog$1.Description, {
		"data-slot": "alert-dialog-description",
		className: cn("text-muted-foreground *:[a]:hover:text-foreground text-xs/relaxed text-balance md:text-pretty *:[a]:underline *:[a]:underline-offset-3", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 122,
		columnNumber: 5
	}, this);
}
function AlertDialogAction({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Button, {
		"data-slot": "alert-dialog-action",
		className: cn(className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 135,
		columnNumber: 5
	}, this);
}
function AlertDialogCancel({ className, variant = "outline", size = "default", ...props }) {
	return /* @__PURE__ */ jsxDEV(AlertDialog$1.Close, {
		"data-slot": "alert-dialog-cancel",
		className: cn(className),
		render: /* @__PURE__ */ jsxDEV(Button, {
			variant,
			size
		}, void 0, false, {
			fileName: _jsxFileName$21,
			lineNumber: 154,
			columnNumber: 15
		}, this),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$21,
		lineNumber: 151,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/avatar.tsx
var _jsxFileName$20 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/avatar.tsx";
function Avatar({ className, size = "default", ...props }) {
	return /* @__PURE__ */ jsxDEV(Avatar$1.Root, {
		"data-slot": "avatar",
		"data-size": size,
		className: cn("size-8 rounded-full after:rounded-full data-[size=lg]:size-10 data-[size=sm]:size-6 after:border-border group/avatar relative flex shrink-0 select-none after:absolute after:inset-0 after:border after:mix-blend-darken dark:after:mix-blend-lighten", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$20,
		lineNumber: 16,
		columnNumber: 5
	}, this);
}
function AvatarImage({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Avatar$1.Image, {
		"data-slot": "avatar-image",
		className: cn("rounded-full aspect-square size-full object-cover", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$20,
		lineNumber: 30,
		columnNumber: 5
	}, this);
}
function AvatarFallback({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Avatar$1.Fallback, {
		"data-slot": "avatar-fallback",
		className: cn("bg-muted text-muted-foreground rounded-full flex size-full items-center justify-center text-sm group-data-[size=sm]/avatar:text-xs", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$20,
		lineNumber: 46,
		columnNumber: 5
	}, this);
}
function AvatarBadge({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("span", {
		"data-slot": "avatar-badge",
		className: cn("bg-primary text-primary-foreground ring-background absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-blend-color ring-2 select-none", "group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden", "group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2", "group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$20,
		lineNumber: 59,
		columnNumber: 5
	}, this);
}
function AvatarGroup({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "avatar-group",
		className: cn("*:data-[slot=avatar]:ring-background group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$20,
		lineNumber: 75,
		columnNumber: 5
	}, this);
}
function AvatarGroupCount({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "avatar-group-count",
		className: cn("bg-muted text-muted-foreground size-8 rounded-full text-xs/relaxed group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3 ring-background relative flex shrink-0 items-center justify-center ring-2", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$20,
		lineNumber: 91,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/badge.tsx
var badgeVariants = cva("h-5 gap-1 rounded-full border border-transparent px-2 py-0.5 text-[0.625rem] font-medium transition-all has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&>svg]:size-2.5! inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-colors overflow-hidden group/badge", {
	variants: { variant: {
		default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
		secondary: "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
		destructive: "bg-destructive/10 [a]:hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 text-destructive dark:bg-destructive/20",
		outline: "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground bg-input/20 dark:bg-input/30",
		ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
		link: "text-primary underline-offset-4 hover:underline"
	} },
	defaultVariants: { variant: "default" }
});
function Badge({ className, variant = "default", render, ...props }) {
	return useRender({
		defaultTagName: "span",
		props: mergeProps({ className: cn(badgeVariants({
			className,
			variant
		})) }, props),
		render,
		state: {
			slot: "badge",
			variant
		}
	});
}
//#endregion
//#region src/components/ui/separator.tsx
var _jsxFileName$19 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/separator.tsx";
function Separator({ className, orientation = "horizontal", ...props }) {
	return /* @__PURE__ */ jsxDEV(Separator$1, {
		"data-slot": "separator",
		orientation,
		className: cn("bg-border shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px data-[orientation=vertical]:self-stretch", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$19,
		lineNumber: 11,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/button-group.tsx
var _jsxFileName$18 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/button-group.tsx";
var buttonGroupVariants = cva("has-[>[data-slot=button-group]]:gap-2 has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md flex w-fit items-stretch [&>*]:focus-visible:z-10 [&>*]:focus-visible:relative [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1", {
	variants: { orientation: {
		horizontal: "[&>[data-slot]:not(:has(~[data-slot]))]:rounded-r-md! [&>[data-slot]~[data-slot]]:rounded-l-none [&>[data-slot]~[data-slot]]:border-l-0 [&>[data-slot]]:rounded-r-none",
		vertical: "[&>[data-slot]:not(:has(~[data-slot]))]:rounded-b-md! flex-col [&>[data-slot]~[data-slot]]:rounded-t-none [&>[data-slot]~[data-slot]]:border-t-0 [&>[data-slot]]:rounded-b-none"
	} },
	defaultVariants: { orientation: "horizontal" }
});
function ButtonGroup({ className, orientation, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		role: "group",
		"data-slot": "button-group",
		"data-orientation": orientation,
		className: cn(buttonGroupVariants({ orientation }), className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$18,
		lineNumber: 31,
		columnNumber: 5
	}, this);
}
function ButtonGroupText({ className, render, ...props }) {
	return useRender({
		defaultTagName: "div",
		props: mergeProps({ className: cn("bg-muted gap-2 rounded-md border px-2.5 text-xs/relaxed font-medium [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none", className) }, props),
		render,
		state: { slot: "button-group-text" }
	});
}
function ButtonGroupSeparator({ className, orientation = "vertical", ...props }) {
	return /* @__PURE__ */ jsxDEV(Separator, {
		"data-slot": "button-group-separator",
		orientation,
		className: cn("bg-input relative self-stretch data-[orientation=horizontal]:mx-px data-[orientation=horizontal]:w-auto data-[orientation=vertical]:my-px data-[orientation=vertical]:h-auto", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$18,
		lineNumber: 70,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/card.tsx
var _jsxFileName$17 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/card.tsx";
function Card({ className, size = "default", ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "card",
		"data-size": size,
		className: cn("ring-foreground/10 bg-card text-card-foreground gap-4 overflow-hidden rounded-lg py-4 text-xs/relaxed ring-1 has-[>img:first-child]:pt-0 data-[size=sm]:gap-3 data-[size=sm]:py-3 *:[img:first-child]:rounded-t-lg *:[img:last-child]:rounded-b-lg group/card flex flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$17,
		lineNumber: 11,
		columnNumber: 5
	}, this);
}
function CardHeader({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "card-header",
		className: cn("gap-1 rounded-t-lg px-4 group-data-[size=sm]/card:px-3 [.border-b]:pb-4 group-data-[size=sm]/card:[.border-b]:pb-3 group/card-header @container/card-header grid auto-rows-min items-start has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto]", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$17,
		lineNumber: 22,
		columnNumber: 5
	}, this);
}
function CardTitle({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "card-title",
		className: cn("text-sm font-medium", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$17,
		lineNumber: 35,
		columnNumber: 5
	}, this);
}
function CardDescription({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "card-description",
		className: cn("text-muted-foreground text-xs/relaxed", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$17,
		lineNumber: 45,
		columnNumber: 5
	}, this);
}
function CardAction({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "card-action",
		className: cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$17,
		lineNumber: 55,
		columnNumber: 5
	}, this);
}
function CardContent({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "card-content",
		className: cn("px-4 group-data-[size=sm]/card:px-3", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$17,
		lineNumber: 68,
		columnNumber: 5
	}, this);
}
function CardFooter({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "card-footer",
		className: cn("rounded-b-lg px-4 group-data-[size=sm]/card:px-3 [.border-t]:pt-4 group-data-[size=sm]/card:[.border-t]:pt-3 flex items-center", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$17,
		lineNumber: 78,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/input.tsx
var _jsxFileName$16 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/input.tsx";
function Input({ className, type, ...props }) {
	return /* @__PURE__ */ jsxDEV(Input$1, {
		type,
		"data-slot": "input",
		className: cn("bg-input/20 dark:bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 h-7 rounded-md border px-2 py-0.5 text-sm transition-colors file:h-6 file:text-xs/relaxed file:font-medium focus-visible:ring-[2px] aria-invalid:ring-[2px] md:text-xs/relaxed file:text-foreground placeholder:text-muted-foreground w-full min-w-0 outline-none file:inline-flex file:border-0 file:bg-transparent disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$16,
		lineNumber: 8,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/textarea.tsx
var _jsxFileName$15 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/textarea.tsx";
function Textarea({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("textarea", {
		"data-slot": "textarea",
		className: cn("border-input bg-input/20 dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 resize-none rounded-md border px-2 py-2 text-sm transition-colors focus-visible:ring-[2px] aria-invalid:ring-[2px] md:text-xs/relaxed placeholder:text-muted-foreground flex field-sizing-content min-h-16 w-full outline-none disabled:cursor-not-allowed disabled:opacity-50", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$15,
		lineNumber: 7,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/input-group.tsx
var _jsxFileName$14 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/input-group.tsx";
function InputGroup({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "input-group",
		role: "group",
		className: cn("border-input bg-input/20 dark:bg-input/30 has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-ring/30 has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40 h-7 rounded-md border transition-colors has-data-[align=block-end]:rounded-md has-data-[align=block-start]:rounded-md has-[[data-slot=input-group-control]:focus-visible]:ring-[2px] has-[[data-slot][aria-invalid=true]]:ring-[2px] has-[textarea]:rounded-md has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5 [[data-slot=combobox-content]_&]:focus-within:border-inherit [[data-slot=combobox-content]_&]:focus-within:ring-0 group/input-group relative flex w-full min-w-0 items-center outline-none has-[>textarea]:h-auto", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$14,
		lineNumber: 11,
		columnNumber: 5
	}, this);
}
var inputGroupAddonVariants = cva("text-muted-foreground **:data-[slot=kbd]:bg-muted-foreground/10 h-auto gap-1 py-2 text-xs/relaxed font-medium group-data-[disabled=true]/input-group:opacity-50 **:data-[slot=kbd]:rounded-[calc(var(--radius-sm)-2px)] **:data-[slot=kbd]:px-1 **:data-[slot=kbd]:text-[0.625rem] [&>svg:not([class*='size-'])]:size-3.5 flex cursor-text items-center justify-center select-none", {
	variants: { align: {
		"inline-start": "pl-2 has-[>button]:ml-[-0.275rem] has-[>kbd]:ml-[-0.275rem] order-first",
		"inline-end": "pr-2 has-[>button]:mr-[-0.275rem] has-[>kbd]:mr-[-0.275rem] order-last",
		"block-start": "px-2 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2 order-first w-full justify-start",
		"block-end": "px-2 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2 order-last w-full justify-start"
	} },
	defaultVariants: { align: "inline-start" }
});
function InputGroupAddon({ className, align = "inline-start", ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		role: "group",
		"data-slot": "input-group-addon",
		"data-align": align,
		className: cn(inputGroupAddonVariants({ align }), className),
		onClick: (e) => {
			if (e.target.closest("button")) return;
			e.currentTarget.parentElement?.querySelector("input")?.focus();
		},
		...props
	}, void 0, false, {
		fileName: _jsxFileName$14,
		lineNumber: 48,
		columnNumber: 5
	}, this);
}
var inputGroupButtonVariants = cva("gap-2 rounded-md text-xs/relaxed shadow-none flex items-center", {
	variants: { size: {
		xs: "h-5 gap-1 rounded-[calc(var(--radius-sm)-2px)] px-1 [&>svg:not([class*='size-'])]:size-3",
		sm: "",
		"icon-xs": "size-6 p-0 has-[>svg]:p-0",
		"icon-sm": "size-8 p-0 has-[>svg]:p-0"
	} },
	defaultVariants: { size: "xs" }
});
function InputGroupButton({ className, type = "button", variant = "ghost", size = "xs", ...props }) {
	return /* @__PURE__ */ jsxDEV(Button, {
		type,
		"data-size": size,
		variant,
		className: cn(inputGroupButtonVariants({ size }), className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$14,
		lineNumber: 92,
		columnNumber: 5
	}, this);
}
function InputGroupText({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("span", {
		className: cn("text-muted-foreground gap-2 text-xs/relaxed [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$14,
		lineNumber: 104,
		columnNumber: 5
	}, this);
}
function InputGroupInput({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Input, {
		"data-slot": "input-group-control",
		className: cn("rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 dark:bg-transparent flex-1", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$14,
		lineNumber: 119,
		columnNumber: 5
	}, this);
}
function InputGroupTextarea({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Textarea, {
		"data-slot": "input-group-control",
		className: cn("rounded-none border-0 bg-transparent py-2 shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 dark:bg-transparent flex-1 resize-none", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$14,
		lineNumber: 132,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/combobox.tsx
var _jsxFileName$13 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/combobox.tsx";
var Combobox = Combobox$1.Root;
function ComboboxValue({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Value, {
		"data-slot": "combobox-value",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 20,
		columnNumber: 10
	}, this);
}
function ComboboxTrigger({ className, children, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Trigger, {
		"data-slot": "combobox-trigger",
		className: cn("[&_svg:not([class*='size-'])]:size-3.5", className),
		...props,
		children: [children, /* @__PURE__ */ jsxDEV(ChevronDownIcon, { className: "text-muted-foreground size-3.5 pointer-events-none" }, void 0, false, {
			fileName: _jsxFileName$13,
			lineNumber: 35,
			columnNumber: 7
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$13,
		lineNumber: 29,
		columnNumber: 5
	}, this);
}
function ComboboxClear({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Clear, {
		"data-slot": "combobox-clear",
		render: /* @__PURE__ */ jsxDEV(InputGroupButton, {
			variant: "ghost",
			size: "icon-xs"
		}, void 0, false, {
			fileName: _jsxFileName$13,
			lineNumber: 44,
			columnNumber: 15
		}, this),
		className: cn(className),
		...props,
		children: /* @__PURE__ */ jsxDEV(XIcon, { className: "pointer-events-none" }, void 0, false, {
			fileName: _jsxFileName$13,
			lineNumber: 48,
			columnNumber: 7
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 42,
		columnNumber: 5
	}, this);
}
function ComboboxInput({ className, children, disabled = false, showTrigger = true, showClear = false, ...props }) {
	return /* @__PURE__ */ jsxDEV(InputGroup, {
		className: cn("w-auto", className),
		children: [
			/* @__PURE__ */ jsxDEV(Combobox$1.Input, {
				render: /* @__PURE__ */ jsxDEV(InputGroupInput, { disabled }, void 0, false, {
					fileName: _jsxFileName$13,
					lineNumber: 67,
					columnNumber: 17
				}, this),
				...props
			}, void 0, false, {
				fileName: _jsxFileName$13,
				lineNumber: 66,
				columnNumber: 7
			}, this),
			/* @__PURE__ */ jsxDEV(InputGroupAddon, {
				align: "inline-end",
				children: [showTrigger && /* @__PURE__ */ jsxDEV(InputGroupButton, {
					size: "icon-xs",
					variant: "ghost",
					render: /* @__PURE__ */ jsxDEV(ComboboxTrigger, {}, void 0, false, {
						fileName: _jsxFileName$13,
						lineNumber: 75,
						columnNumber: 21
					}, this),
					"data-slot": "input-group-button",
					className: "group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent",
					disabled
				}, void 0, false, {
					fileName: _jsxFileName$13,
					lineNumber: 72,
					columnNumber: 11
				}, this), showClear && /* @__PURE__ */ jsxDEV(ComboboxClear, { disabled }, void 0, false, {
					fileName: _jsxFileName$13,
					lineNumber: 81,
					columnNumber: 23
				}, this)]
			}, void 0, true, {
				fileName: _jsxFileName$13,
				lineNumber: 70,
				columnNumber: 7
			}, this),
			children
		]
	}, void 0, true, {
		fileName: _jsxFileName$13,
		lineNumber: 65,
		columnNumber: 5
	}, this);
}
function ComboboxContent({ className, side = "bottom", sideOffset = 6, align = "start", alignOffset = 0, anchor, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Portal, { children: /* @__PURE__ */ jsxDEV(Combobox$1.Positioner, {
		side,
		sideOffset,
		align,
		alignOffset,
		anchor,
		className: "isolate z-50",
		children: /* @__PURE__ */ jsxDEV(Combobox$1.Popup, {
			"data-slot": "combobox-content",
			"data-chips": !!anchor,
			className: cn("bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 *:data-[slot=input-group]:bg-input/20 dark:bg-input/30 max-h-72 min-w-32 overflow-hidden rounded-lg shadow-md ring-1 duration-100 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-7 *:data-[slot=input-group]:border-none *:data-[slot=input-group]:shadow-none group/combobox-content relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) data-[chips=true]:min-w-(--anchor-width)", className),
			...props
		}, void 0, false, {
			fileName: _jsxFileName$13,
			lineNumber: 111,
			columnNumber: 9
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 103,
		columnNumber: 7
	}, this) }, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 102,
		columnNumber: 5
	}, this);
}
function ComboboxList({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.List, {
		"data-slot": "combobox-list",
		className: cn("no-scrollbar max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto p-1 data-empty:p-0 overflow-y-auto overscroll-contain", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 127,
		columnNumber: 5
	}, this);
}
function ComboboxItem({ className, children, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Item, {
		"data-slot": "combobox-item",
		className: cn("data-highlighted:bg-accent data-highlighted:text-accent-foreground not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground min-h-7 gap-2 rounded-md px-2 py-1 text-xs/relaxed [&_svg:not([class*='size-'])]:size-3.5 relative flex w-full cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0", className),
		...props,
		children: [children, /* @__PURE__ */ jsxDEV(Combobox$1.ItemIndicator, {
			render: /* @__PURE__ */ jsxDEV("span", { className: "pointer-events-none absolute right-2 flex items-center justify-center" }, void 0, false, {
				fileName: _jsxFileName$13,
				lineNumber: 155,
				columnNumber: 11
			}, this),
			children: /* @__PURE__ */ jsxDEV(CheckIcon, { className: "pointer-events-none" }, void 0, false, {
				fileName: _jsxFileName$13,
				lineNumber: 158,
				columnNumber: 9
			}, this)
		}, void 0, false, {
			fileName: _jsxFileName$13,
			lineNumber: 153,
			columnNumber: 7
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$13,
		lineNumber: 144,
		columnNumber: 5
	}, this);
}
function ComboboxGroup({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Group, {
		"data-slot": "combobox-group",
		className: cn(className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 166,
		columnNumber: 5
	}, this);
}
function ComboboxLabel({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.GroupLabel, {
		"data-slot": "combobox-label",
		className: cn("text-muted-foreground px-2 py-1.5 text-xs", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 179,
		columnNumber: 5
	}, this);
}
function ComboboxCollection({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Collection, {
		"data-slot": "combobox-collection",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 189,
		columnNumber: 5
	}, this);
}
function ComboboxEmpty({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Empty, {
		"data-slot": "combobox-empty",
		className: cn("text-muted-foreground hidden w-full justify-center py-2 text-center text-xs/relaxed group-data-empty/combobox-content:flex", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 195,
		columnNumber: 5
	}, this);
}
function ComboboxSeparator({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Separator, {
		"data-slot": "combobox-separator",
		className: cn("bg-border/50 -mx-1 my-1 h-px", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 211,
		columnNumber: 5
	}, this);
}
function ComboboxChips({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Chips, {
		"data-slot": "combobox-chips",
		className: cn("bg-input/20 dark:bg-input/30 border-input focus-within:border-ring focus-within:ring-ring/30 has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40 has-aria-invalid:border-destructive dark:has-aria-invalid:border-destructive/50 flex min-h-7 flex-wrap items-center gap-1 rounded-md border bg-clip-padding px-2 py-0.5 text-xs/relaxed transition-colors focus-within:ring-[2px] has-aria-invalid:ring-[2px] has-data-[slot=combobox-chip]:px-1", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 225,
		columnNumber: 5
	}, this);
}
function ComboboxChip({ className, children, showRemove = true, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Chip, {
		"data-slot": "combobox-chip",
		className: cn("bg-muted-foreground/10 text-foreground flex h-[calc(--spacing(4.75))] w-fit items-center justify-center gap-1 rounded-[calc(var(--radius-sm)-2px)] px-1.5 text-xs/relaxed font-medium whitespace-nowrap has-data-[slot=combobox-chip-remove]:pr-0 has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50", className),
		...props,
		children: [children, showRemove && /* @__PURE__ */ jsxDEV(Combobox$1.ChipRemove, {
			render: /* @__PURE__ */ jsxDEV(Button, {
				variant: "ghost",
				size: "icon-xs"
			}, void 0, false, {
				fileName: _jsxFileName$13,
				lineNumber: 256,
				columnNumber: 19
			}, this),
			className: "-ml-1 opacity-50 hover:opacity-100",
			"data-slot": "combobox-chip-remove",
			children: /* @__PURE__ */ jsxDEV(XIcon, { className: "pointer-events-none" }, void 0, false, {
				fileName: _jsxFileName$13,
				lineNumber: 260,
				columnNumber: 11
			}, this)
		}, void 0, false, {
			fileName: _jsxFileName$13,
			lineNumber: 255,
			columnNumber: 9
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$13,
		lineNumber: 245,
		columnNumber: 5
	}, this);
}
function ComboboxChipsInput({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Combobox$1.Input, {
		"data-slot": "combobox-chip-input",
		className: cn("min-w-16 flex-1 outline-none", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$13,
		lineNumber: 272,
		columnNumber: 5
	}, this);
}
function useComboboxAnchor() {
	return React.useRef(null);
}
//#endregion
//#region src/components/ui/dialog.tsx
var _jsxFileName$12 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/dialog.tsx";
function Dialog({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Root, {
		"data-slot": "dialog",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 11,
		columnNumber: 10
	}, this);
}
function DialogTrigger({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Trigger, {
		"data-slot": "dialog-trigger",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 15,
		columnNumber: 10
	}, this);
}
function DialogPortal({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Portal, {
		"data-slot": "dialog-portal",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 19,
		columnNumber: 10
	}, this);
}
function DialogClose({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Close, {
		"data-slot": "dialog-close",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 23,
		columnNumber: 10
	}, this);
}
function DialogOverlay({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Backdrop, {
		"data-slot": "dialog-overlay",
		className: cn("data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/80 duration-100 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 isolate z-50", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 31,
		columnNumber: 5
	}, this);
}
function DialogContent({ className, children, showCloseButton = true, ...props }) {
	return /* @__PURE__ */ jsxDEV(DialogPortal, { children: [/* @__PURE__ */ jsxDEV(DialogOverlay, {}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 49,
		columnNumber: 7
	}, this), /* @__PURE__ */ jsxDEV(Dialog$1.Popup, {
		"data-slot": "dialog-content",
		className: cn("bg-background data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 ring-foreground/10 grid max-w-[calc(100%-2rem)] gap-4 rounded-xl p-4 text-xs/relaxed ring-1 duration-100 sm:max-w-sm fixed top-1/2 left-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 outline-none", className),
		...props,
		children: [children, showCloseButton && /* @__PURE__ */ jsxDEV(Dialog$1.Close, {
			"data-slot": "dialog-close",
			render: /* @__PURE__ */ jsxDEV(Button, {
				variant: "ghost",
				className: "absolute top-2 right-2",
				size: "icon-sm"
			}, void 0, false, {
				fileName: _jsxFileName$12,
				lineNumber: 63,
				columnNumber: 15
			}, this),
			children: [/* @__PURE__ */ jsxDEV(XIcon, {}, void 0, false, {
				fileName: _jsxFileName$12,
				lineNumber: 70,
				columnNumber: 13
			}, this), /* @__PURE__ */ jsxDEV("span", {
				className: "sr-only",
				children: "Close"
			}, void 0, false, {
				fileName: _jsxFileName$12,
				lineNumber: 72,
				columnNumber: 13
			}, this)]
		}, void 0, true, {
			fileName: _jsxFileName$12,
			lineNumber: 60,
			columnNumber: 11
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$12,
		lineNumber: 50,
		columnNumber: 7
	}, this)] }, void 0, true, {
		fileName: _jsxFileName$12,
		lineNumber: 48,
		columnNumber: 5
	}, this);
}
function DialogHeader({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "dialog-header",
		className: cn("gap-1 flex flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 82,
		columnNumber: 5
	}, this);
}
function DialogFooter({ className, showCloseButton = false, children, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "dialog-footer",
		className: cn("gap-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
		...props,
		children: [children, showCloseButton && /* @__PURE__ */ jsxDEV(Dialog$1.Close, {
			render: /* @__PURE__ */ jsxDEV(Button, { variant: "outline" }, void 0, false, {
				fileName: _jsxFileName$12,
				lineNumber: 109,
				columnNumber: 40
			}, this),
			children: "Close"
		}, void 0, false, {
			fileName: _jsxFileName$12,
			lineNumber: 109,
			columnNumber: 9
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$12,
		lineNumber: 99,
		columnNumber: 5
	}, this);
}
function DialogTitle({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Title, {
		"data-slot": "dialog-title",
		className: cn("text-sm font-medium", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 119,
		columnNumber: 5
	}, this);
}
function DialogDescription({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Description, {
		"data-slot": "dialog-description",
		className: cn("text-muted-foreground *:[a]:hover:text-foreground text-xs/relaxed *:[a]:underline *:[a]:underline-offset-3", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$12,
		lineNumber: 132,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/dropdown-menu.tsx
var _jsxFileName$11 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/dropdown-menu.tsx";
function DropdownMenu({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.Root, {
		"data-slot": "dropdown-menu",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 8,
		columnNumber: 10
	}, this);
}
function DropdownMenuPortal({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.Portal, {
		"data-slot": "dropdown-menu-portal",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 12,
		columnNumber: 10
	}, this);
}
function DropdownMenuTrigger({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.Trigger, {
		"data-slot": "dropdown-menu-trigger",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 16,
		columnNumber: 10
	}, this);
}
function DropdownMenuContent({ align = "start", alignOffset = 0, side = "bottom", sideOffset = 4, className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.Portal, { children: /* @__PURE__ */ jsxDEV(Menu.Positioner, {
		className: "isolate z-50 outline-none",
		align,
		alignOffset,
		side,
		sideOffset,
		children: /* @__PURE__ */ jsxDEV(Menu.Popup, {
			"data-slot": "dropdown-menu-content",
			className: cn("data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 bg-popover text-popover-foreground min-w-32 rounded-lg p-1 shadow-md ring-1 duration-100 z-50 max-h-(--available-height) w-(--anchor-width) origin-(--transform-origin) overflow-x-hidden overflow-y-auto outline-none data-closed:overflow-hidden", className),
			...props
		}, void 0, false, {
			fileName: _jsxFileName$11,
			lineNumber: 40,
			columnNumber: 9
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 33,
		columnNumber: 7
	}, this) }, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 32,
		columnNumber: 5
	}, this);
}
function DropdownMenuGroup({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.Group, {
		"data-slot": "dropdown-menu-group",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 51,
		columnNumber: 10
	}, this);
}
function DropdownMenuLabel({ className, inset, ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.GroupLabel, {
		"data-slot": "dropdown-menu-label",
		"data-inset": inset,
		className: cn("text-muted-foreground px-2 py-1.5 text-xs data-[inset]:pl-8", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 62,
		columnNumber: 5
	}, this);
}
function DropdownMenuItem({ className, inset, variant = "default", ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.Item, {
		"data-slot": "dropdown-menu-item",
		"data-inset": inset,
		"data-variant": variant,
		className: cn("focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:text-destructive not-data-[variant=destructive]:focus:**:text-accent-foreground min-h-7 gap-2 rounded-md px-2 py-1 text-xs/relaxed [&_svg:not([class*='size-'])]:size-3.5 group/dropdown-menu-item relative flex cursor-default items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 81,
		columnNumber: 5
	}, this);
}
function DropdownMenuSub({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.SubmenuRoot, {
		"data-slot": "dropdown-menu-sub",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 95,
		columnNumber: 10
	}, this);
}
function DropdownMenuSubTrigger({ className, inset, children, ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.SubmenuTrigger, {
		"data-slot": "dropdown-menu-sub-trigger",
		"data-inset": inset,
		className: cn("focus:bg-accent focus:text-accent-foreground data-open:bg-accent data-open:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground min-h-7 gap-2 rounded-md px-2 py-1 text-xs [&_svg:not([class*='size-'])]:size-3.5 flex cursor-default items-center outline-hidden select-none data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0", className),
		...props,
		children: [children, /* @__PURE__ */ jsxDEV(ChevronRightIcon, { className: "ml-auto" }, void 0, false, {
			fileName: _jsxFileName$11,
			lineNumber: 117,
			columnNumber: 7
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$11,
		lineNumber: 107,
		columnNumber: 5
	}, this);
}
function DropdownMenuSubContent({ align = "start", alignOffset = -3, side = "right", sideOffset = 0, className, ...props }) {
	return /* @__PURE__ */ jsxDEV(DropdownMenuContent, {
		"data-slot": "dropdown-menu-sub-content",
		className: cn("data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 bg-popover text-popover-foreground min-w-32 rounded-lg p-1 shadow-md ring-1 duration-100 w-auto", className),
		align,
		alignOffset,
		side,
		sideOffset,
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 131,
		columnNumber: 5
	}, this);
}
function DropdownMenuCheckboxItem({ className, children, checked, ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.CheckboxItem, {
		"data-slot": "dropdown-menu-checkbox-item",
		className: cn("focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground min-h-7 gap-2 rounded-md py-1.5 pr-8 pl-2 text-xs [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0", className),
		checked,
		...props,
		children: [/* @__PURE__ */ jsxDEV("span", {
			className: "pointer-events-none absolute right-2 flex items-center justify-center pointer-events-none",
			"data-slot": "dropdown-menu-checkbox-item-indicator",
			children: /* @__PURE__ */ jsxDEV(Menu.CheckboxItemIndicator, { children: /* @__PURE__ */ jsxDEV(CheckIcon, {}, void 0, false, {
				fileName: _jsxFileName$11,
				lineNumber: 164,
				columnNumber: 11
			}, this) }, void 0, false, {
				fileName: _jsxFileName$11,
				lineNumber: 163,
				columnNumber: 9
			}, this)
		}, void 0, false, {
			fileName: _jsxFileName$11,
			lineNumber: 159,
			columnNumber: 7
		}, this), children]
	}, void 0, true, {
		fileName: _jsxFileName$11,
		lineNumber: 150,
		columnNumber: 5
	}, this);
}
function DropdownMenuRadioGroup({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.RadioGroup, {
		"data-slot": "dropdown-menu-radio-group",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 175,
		columnNumber: 5
	}, this);
}
function DropdownMenuRadioItem({ className, children, ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.RadioItem, {
		"data-slot": "dropdown-menu-radio-item",
		className: cn("focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground min-h-7 gap-2 rounded-md py-1.5 pr-8 pl-2 text-xs [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0", className),
		...props,
		children: [/* @__PURE__ */ jsxDEV("span", {
			className: "pointer-events-none absolute right-2 flex items-center justify-center pointer-events-none",
			"data-slot": "dropdown-menu-radio-item-indicator",
			children: /* @__PURE__ */ jsxDEV(Menu.RadioItemIndicator, { children: /* @__PURE__ */ jsxDEV(CheckIcon, {}, void 0, false, {
				fileName: _jsxFileName$11,
				lineNumber: 201,
				columnNumber: 11
			}, this) }, void 0, false, {
				fileName: _jsxFileName$11,
				lineNumber: 200,
				columnNumber: 9
			}, this)
		}, void 0, false, {
			fileName: _jsxFileName$11,
			lineNumber: 196,
			columnNumber: 7
		}, this), children]
	}, void 0, true, {
		fileName: _jsxFileName$11,
		lineNumber: 188,
		columnNumber: 5
	}, this);
}
function DropdownMenuSeparator({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Menu.Separator, {
		"data-slot": "dropdown-menu-separator",
		className: cn("bg-border/50 -mx-1 my-1 h-px", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 215,
		columnNumber: 5
	}, this);
}
function DropdownMenuShortcut({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("span", {
		"data-slot": "dropdown-menu-shortcut",
		className: cn("text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground ml-auto text-[0.625rem] tracking-widest", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$11,
		lineNumber: 228,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/label.tsx
var _jsxFileName$10 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/label.tsx";
function Label({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("label", {
		"data-slot": "label",
		className: cn("gap-2 text-xs/relaxed leading-none font-medium group-data-[disabled=true]:opacity-50 peer-disabled:opacity-50 flex items-center select-none group-data-[disabled=true]:pointer-events-none peer-disabled:cursor-not-allowed", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$10,
		lineNumber: 9,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/field.tsx
var _jsxFileName$9 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/field.tsx";
function FieldSet({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("fieldset", {
		"data-slot": "field-set",
		className: cn("gap-4 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3 flex flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 12,
		columnNumber: 5
	}, this);
}
function FieldLegend({ className, variant = "legend", ...props }) {
	return /* @__PURE__ */ jsxDEV("legend", {
		"data-slot": "field-legend",
		"data-variant": variant,
		className: cn("mb-2 font-medium data-[variant=label]:text-xs/relaxed data-[variant=legend]:text-sm", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 26,
		columnNumber: 5
	}, this);
}
function FieldGroup({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "field-group",
		className: cn("gap-4 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4 group/field-group @container/field-group flex w-full flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 37,
		columnNumber: 5
	}, this);
}
var fieldVariants = cva("data-[invalid=true]:text-destructive gap-2 group/field flex w-full", {
	variants: { orientation: {
		vertical: "flex-col [&>*]:w-full [&>.sr-only]:w-auto",
		horizontal: "flex-row items-center [&>[data-slot=field-label]]:flex-auto has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
		responsive: "flex-col [&>*]:w-full [&>.sr-only]:w-auto @md/field-group:flex-row @md/field-group:items-center @md/field-group:[&>*]:w-auto @md/field-group:[&>[data-slot=field-label]]:flex-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px"
	} },
	defaultVariants: { orientation: "vertical" }
});
function Field({ className, orientation = "vertical", ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		role: "group",
		"data-slot": "field",
		"data-orientation": orientation,
		className: cn(fieldVariants({ orientation }), className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 70,
		columnNumber: 5
	}, this);
}
function FieldContent({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "field-content",
		className: cn("gap-0.5 group/field-content flex flex-1 flex-col leading-snug", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 82,
		columnNumber: 5
	}, this);
}
function FieldLabel({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Label, {
		"data-slot": "field-label",
		className: cn("has-data-checked:bg-primary/5 dark:has-data-checked:bg-primary/10 gap-2 group-data-[disabled=true]/field:opacity-50 has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border [&>*]:data-[slot=field]:p-2 group/field-label peer/field-label flex w-fit leading-snug", "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 98,
		columnNumber: 5
	}, this);
}
function FieldTitle({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "field-label",
		className: cn("gap-2 text-xs/relaxed font-medium group-data-[disabled=true]/field:opacity-50 flex w-fit items-center leading-snug", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 112,
		columnNumber: 5
	}, this);
}
function FieldDescription({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("p", {
		"data-slot": "field-description",
		className: cn("text-muted-foreground text-left text-xs/relaxed [[data-variant=legend]+&]:-mt-1.5 leading-normal font-normal group-has-[[data-orientation=horizontal]]/field:text-balance", "last:mt-0 nth-last-2:-mt-1", "[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 125,
		columnNumber: 5
	}, this);
}
function FieldSeparator({ children, className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "field-separator",
		"data-content": !!children,
		className: cn("-my-2 h-5 text-xs/relaxed group-data-[variant=outline]/field-group:-mb-2 relative", className),
		...props,
		children: [/* @__PURE__ */ jsxDEV(Separator, { className: "absolute inset-0 top-1/2" }, void 0, false, {
			fileName: _jsxFileName$9,
			lineNumber: 152,
			columnNumber: 7
		}, this), children && /* @__PURE__ */ jsxDEV("span", {
			className: "text-muted-foreground px-2 bg-background relative mx-auto block w-fit",
			"data-slot": "field-separator-content",
			children
		}, void 0, false, {
			fileName: _jsxFileName$9,
			lineNumber: 154,
			columnNumber: 9
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$9,
		lineNumber: 146,
		columnNumber: 5
	}, this);
}
function FieldError({ className, children, errors, ...props }) {
	const content = useMemo(() => {
		if (children) return children;
		if (!errors?.length) return null;
		const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()];
		if (uniqueErrors?.length == 1) return uniqueErrors[0]?.message;
		return /* @__PURE__ */ jsxDEV("ul", {
			className: "ml-4 flex list-disc flex-col gap-1",
			children: uniqueErrors.map((error, index) => error?.message && /* @__PURE__ */ jsxDEV("li", { children: error.message }, index, false, {
				fileName: _jsxFileName$9,
				lineNumber: 194,
				columnNumber: 31
			}, this))
		}, void 0, false, {
			fileName: _jsxFileName$9,
			lineNumber: 191,
			columnNumber: 7
		}, this);
	}, [children, errors]);
	if (!content) return null;
	return /* @__PURE__ */ jsxDEV("div", {
		role: "alert",
		"data-slot": "field-error",
		className: cn("text-destructive text-xs/relaxed font-normal", className),
		...props,
		children: content
	}, void 0, false, {
		fileName: _jsxFileName$9,
		lineNumber: 205,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/form-manual-fix.tsx
var _jsxFileName$8 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/form-manual-fix.tsx";
var Form = FormProvider;
var FormFieldContext = React.createContext({});
var FormField = ({ ...props }) => {
	return /* @__PURE__ */ jsxDEV(FormFieldContext.Provider, {
		value: { name: props.name },
		children: /* @__PURE__ */ jsxDEV(Controller, { ...props }, void 0, false, {
			fileName: _jsxFileName$8,
			lineNumber: 36,
			columnNumber: 7
		}, void 0)
	}, void 0, false, {
		fileName: _jsxFileName$8,
		lineNumber: 35,
		columnNumber: 5
	}, void 0);
};
var useFormField = () => {
	const fieldContext = React.useContext(FormFieldContext);
	const itemContext = React.useContext(FormItemContext);
	const { getFieldState } = useFormContext();
	const formState = useFormState({ name: fieldContext.name });
	const fieldState = getFieldState(fieldContext.name, formState);
	if (!fieldContext) throw new Error("useFormField should be used within <FormField>");
	const { id } = itemContext;
	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldState
	};
};
var FormItemContext = React.createContext({});
function FormItem({ className, ...props }) {
	const id = React.useId();
	return /* @__PURE__ */ jsxDEV(FormItemContext.Provider, {
		value: { id },
		children: /* @__PURE__ */ jsxDEV("div", {
			"data-slot": "form-item",
			className: cn("grid gap-2", className),
			...props
		}, void 0, false, {
			fileName: _jsxFileName$8,
			lineNumber: 77,
			columnNumber: 7
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$8,
		lineNumber: 76,
		columnNumber: 5
	}, this);
}
function FormLabel({ className, ...props }) {
	const { error, formItemId } = useFormField();
	return /* @__PURE__ */ jsxDEV(Label, {
		"data-slot": "form-label",
		"data-error": !!error,
		className: cn("data-[error=true]:text-destructive", className),
		htmlFor: formItemId,
		...props
	}, void 0, false, {
		fileName: _jsxFileName$8,
		lineNumber: 93,
		columnNumber: 5
	}, this);
}
function FormControl({ ...props }) {
	const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "form-control",
		id: formItemId,
		"aria-describedby": !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`,
		"aria-invalid": !!error,
		...props
	}, void 0, false, {
		fileName: _jsxFileName$8,
		lineNumber: 108,
		columnNumber: 5
	}, this);
}
function FormDescription({ className, ...props }) {
	const { formDescriptionId } = useFormField();
	return /* @__PURE__ */ jsxDEV("p", {
		"data-slot": "form-description",
		id: formDescriptionId,
		className: cn("text-muted-foreground text-sm", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$8,
		lineNumber: 126,
		columnNumber: 5
	}, this);
}
function FormMessage({ className, ...props }) {
	const { error, formMessageId } = useFormField();
	const body = error ? String(error?.message ?? "") : props.children;
	return /* @__PURE__ */ jsxDEV("p", {
		"data-slot": "form-message",
		id: formMessageId,
		className: cn(!body && "opacity-0", "text-destructive text-sm", className),
		...props,
		children: body ?? "."
	}, void 0, false, {
		fileName: _jsxFileName$8,
		lineNumber: 144,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/input-otp.tsx
var _jsxFileName$7 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/input-otp.tsx";
function InputOTP({ className, containerClassName, ...props }) {
	return /* @__PURE__ */ jsxDEV(OTPInput, {
		"data-slot": "input-otp",
		containerClassName: cn("cn-input-otp flex items-center has-disabled:opacity-50", containerClassName),
		spellCheck: false,
		className: cn("disabled:cursor-not-allowed", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$7,
		lineNumber: 15,
		columnNumber: 5
	}, this);
}
function InputOTPGroup({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "input-otp-group",
		className: cn("has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40 has-aria-invalid:border-destructive rounded-md has-aria-invalid:ring-[2px] flex items-center", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$7,
		lineNumber: 33,
		columnNumber: 5
	}, this);
}
function InputOTPSlot({ index, className, ...props }) {
	const { char, hasFakeCaret, isActive } = React.useContext(OTPInputContext)?.slots[index] ?? {};
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "input-otp-slot",
		"data-active": isActive,
		className: cn("bg-input/20 dark:bg-input/30 border-input data-[active=true]:border-ring data-[active=true]:ring-ring/30 data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40 aria-invalid:border-destructive data-[active=true]:aria-invalid:border-destructive size-7 border-y border-r text-xs/relaxed transition-all outline-none first:rounded-l-md first:border-l last:rounded-r-md data-[active=true]:ring-[2px] relative flex items-center justify-center data-[active=true]:z-10", className),
		...props,
		children: [char, hasFakeCaret && /* @__PURE__ */ jsxDEV("div", {
			className: "pointer-events-none absolute inset-0 flex items-center justify-center",
			children: /* @__PURE__ */ jsxDEV("div", { className: "animate-caret-blink bg-foreground h-4 w-px duration-1000 bg-foreground h-4 w-px" }, void 0, false, {
				fileName: _jsxFileName$7,
				lineNumber: 64,
				columnNumber: 11
			}, this)
		}, void 0, false, {
			fileName: _jsxFileName$7,
			lineNumber: 63,
			columnNumber: 9
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$7,
		lineNumber: 52,
		columnNumber: 5
	}, this);
}
function InputOTPSeparator({ ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "input-otp-separator",
		className: "[&_svg:not([class*='size-'])]:size-4 flex items-center",
		role: "separator",
		...props,
		children: /* @__PURE__ */ jsxDEV(MinusIcon, {}, void 0, false, {
			fileName: _jsxFileName$7,
			lineNumber: 79,
			columnNumber: 7
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$7,
		lineNumber: 73,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/select.tsx
var _jsxFileName$6 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/select.tsx";
var Select = Select$1.Root;
function SelectGroup({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.Group, {
		"data-slot": "select-group",
		className: cn("scroll-my-1 p-1", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 13,
		columnNumber: 5
	}, this);
}
function SelectValue({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.Value, {
		"data-slot": "select-value",
		className: cn("flex flex-1 text-left", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 23,
		columnNumber: 5
	}, this);
}
function SelectTrigger({ className, size = "default", children, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.Trigger, {
		"data-slot": "select-trigger",
		"data-size": size,
		className: cn("border-input data-[placeholder]:text-muted-foreground bg-input/20 dark:bg-input/30 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 gap-1.5 rounded-md border px-2 py-1.5 text-xs/relaxed transition-colors focus-visible:ring-[2px] aria-invalid:ring-[2px] data-[size=default]:h-7 data-[size=sm]:h-6 *:data-[slot=select-value]:flex *:data-[slot=select-value]:gap-1.5 [&_svg:not([class*='size-'])]:size-3.5 flex w-fit items-center justify-between whitespace-nowrap outline-none disabled:cursor-not-allowed disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center [&_svg]:pointer-events-none [&_svg]:shrink-0", className),
		...props,
		children: [children, /* @__PURE__ */ jsxDEV(Select$1.Icon, { render: /* @__PURE__ */ jsxDEV(ChevronDownIcon, { className: "text-muted-foreground size-3.5 pointer-events-none" }, void 0, false, {
			fileName: _jsxFileName$6,
			lineNumber: 52,
			columnNumber: 11
		}, this) }, void 0, false, {
			fileName: _jsxFileName$6,
			lineNumber: 50,
			columnNumber: 7
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$6,
		lineNumber: 40,
		columnNumber: 5
	}, this);
}
function SelectContent({ className, children, side = "bottom", sideOffset = 4, align = "center", alignOffset = 0, alignItemWithTrigger = true, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.Portal, { children: /* @__PURE__ */ jsxDEV(Select$1.Positioner, {
		side,
		sideOffset,
		align,
		alignOffset,
		alignItemWithTrigger,
		className: "isolate z-50",
		children: /* @__PURE__ */ jsxDEV(Select$1.Popup, {
			"data-slot": "select-content",
			className: cn("bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 min-w-32 rounded-lg shadow-md ring-1 duration-100 relative isolate z-50 max-h-(--available-height) w-(--anchor-width) origin-(--transform-origin) overflow-x-hidden overflow-y-auto", className),
			...props,
			children: [
				/* @__PURE__ */ jsxDEV(SelectScrollUpButton, {}, void 0, false, {
					fileName: _jsxFileName$6,
					lineNumber: 88,
					columnNumber: 11
				}, this),
				/* @__PURE__ */ jsxDEV(Select$1.List, { children }, void 0, false, {
					fileName: _jsxFileName$6,
					lineNumber: 89,
					columnNumber: 11
				}, this),
				/* @__PURE__ */ jsxDEV(SelectScrollDownButton, {}, void 0, false, {
					fileName: _jsxFileName$6,
					lineNumber: 90,
					columnNumber: 11
				}, this)
			]
		}, void 0, true, {
			fileName: _jsxFileName$6,
			lineNumber: 83,
			columnNumber: 9
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 75,
		columnNumber: 7
	}, this) }, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 74,
		columnNumber: 5
	}, this);
}
function SelectLabel({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.GroupLabel, {
		"data-slot": "select-label",
		className: cn("text-muted-foreground px-2 py-1.5 text-xs", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 102,
		columnNumber: 5
	}, this);
}
function SelectItem({ className, children, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.Item, {
		"data-slot": "select-item",
		className: cn("focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground min-h-7 gap-2 rounded-md px-2 py-1 text-xs/relaxed [&_svg:not([class*='size-'])]:size-3.5 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 relative flex w-full cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0", className),
		...props,
		children: [/* @__PURE__ */ jsxDEV(Select$1.ItemText, {
			className: "flex flex-1 gap-2 shrink-0 whitespace-nowrap",
			children
		}, void 0, false, {
			fileName: _jsxFileName$6,
			lineNumber: 124,
			columnNumber: 7
		}, this), /* @__PURE__ */ jsxDEV(Select$1.ItemIndicator, {
			render: /* @__PURE__ */ jsxDEV("span", { className: "pointer-events-none absolute right-2 flex items-center justify-center" }, void 0, false, {
				fileName: _jsxFileName$6,
				lineNumber: 128,
				columnNumber: 17
			}, this),
			children: /* @__PURE__ */ jsxDEV(CheckIcon, { className: "pointer-events-none" }, void 0, false, {
				fileName: _jsxFileName$6,
				lineNumber: 130,
				columnNumber: 9
			}, this)
		}, void 0, false, {
			fileName: _jsxFileName$6,
			lineNumber: 127,
			columnNumber: 7
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$6,
		lineNumber: 116,
		columnNumber: 5
	}, this);
}
function SelectSeparator({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.Separator, {
		"data-slot": "select-separator",
		className: cn("bg-border/50 -mx-1 my-1 h-px pointer-events-none", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 141,
		columnNumber: 5
	}, this);
}
function SelectScrollUpButton({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.ScrollUpArrow, {
		"data-slot": "select-scroll-up-button",
		className: cn("bg-popover z-10 flex cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-3.5 top-0 w-full", className),
		...props,
		children: /* @__PURE__ */ jsxDEV(ChevronUpIcon, {}, void 0, false, {
			fileName: _jsxFileName$6,
			lineNumber: 159,
			columnNumber: 7
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 154,
		columnNumber: 5
	}, this);
}
function SelectScrollDownButton({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Select$1.ScrollDownArrow, {
		"data-slot": "select-scroll-down-button",
		className: cn("bg-popover z-10 flex cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-3.5 bottom-0 w-full", className),
		...props,
		children: /* @__PURE__ */ jsxDEV(ChevronDownIcon, {}, void 0, false, {
			fileName: _jsxFileName$6,
			lineNumber: 175,
			columnNumber: 7
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 170,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/sheet.tsx
var _jsxFileName$5 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/sheet.tsx";
function Sheet({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Root, {
		"data-slot": "sheet",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 9,
		columnNumber: 10
	}, this);
}
function SheetPortal({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Portal, {
		"data-slot": "sheet-portal",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 21,
		columnNumber: 10
	}, this);
}
function SheetOverlay({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Backdrop, {
		"data-slot": "sheet-overlay",
		className: cn("data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/80 duration-100 data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 z-50", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 26,
		columnNumber: 5
	}, this);
}
function SheetContent({ className, children, side = "right", showCloseButton = true, ...props }) {
	return /* @__PURE__ */ jsxDEV(SheetPortal, { children: [/* @__PURE__ */ jsxDEV(SheetOverlay, {}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 46,
		columnNumber: 7
	}, this), /* @__PURE__ */ jsxDEV(Dialog$1.Popup, {
		"data-slot": "sheet-content",
		"data-side": side,
		className: cn("bg-background data-open:animate-in data-closed:animate-out data-[side=right]:data-closed:slide-out-to-right-10 data-[side=right]:data-open:slide-in-from-right-10 data-[side=left]:data-closed:slide-out-to-left-10 data-[side=left]:data-open:slide-in-from-left-10 data-[side=top]:data-closed:slide-out-to-top-10 data-[side=top]:data-open:slide-in-from-top-10 data-closed:fade-out-0 data-open:fade-in-0 data-[side=bottom]:data-closed:slide-out-to-bottom-10 data-[side=bottom]:data-open:slide-in-from-bottom-10 fixed z-50 flex flex-col bg-clip-padding text-xs/relaxed shadow-lg transition duration-200 ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm", className),
		...props,
		children: [children, showCloseButton && /* @__PURE__ */ jsxDEV(Dialog$1.Close, {
			"data-slot": "sheet-close",
			render: /* @__PURE__ */ jsxDEV(Button, {
				variant: "ghost",
				className: "absolute top-4 right-4",
				size: "icon-sm"
			}, void 0, false, {
				fileName: _jsxFileName$5,
				lineNumber: 58,
				columnNumber: 15
			}, this),
			children: [/* @__PURE__ */ jsxDEV(XIcon, {}, void 0, false, {
				fileName: _jsxFileName$5,
				lineNumber: 65,
				columnNumber: 13
			}, this), /* @__PURE__ */ jsxDEV("span", {
				className: "sr-only",
				children: "Close"
			}, void 0, false, {
				fileName: _jsxFileName$5,
				lineNumber: 67,
				columnNumber: 13
			}, this)]
		}, void 0, true, {
			fileName: _jsxFileName$5,
			lineNumber: 55,
			columnNumber: 11
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$5,
		lineNumber: 47,
		columnNumber: 7
	}, this)] }, void 0, true, {
		fileName: _jsxFileName$5,
		lineNumber: 45,
		columnNumber: 5
	}, this);
}
function SheetHeader({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sheet-header",
		className: cn("gap-1.5 p-6 flex flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 77,
		columnNumber: 5
	}, this);
}
function SheetTitle({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Title, {
		"data-slot": "sheet-title",
		className: cn("text-foreground text-sm font-medium", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 97,
		columnNumber: 5
	}, this);
}
function SheetDescription({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Dialog$1.Description, {
		"data-slot": "sheet-description",
		className: cn("text-muted-foreground text-xs/relaxed", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 110,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/skeleton.tsx
var _jsxFileName$4 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/skeleton.tsx";
function Skeleton({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "skeleton",
		className: cn("bg-muted rounded-md animate-pulse", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$4,
		lineNumber: 5,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/components/ui/tooltip.tsx
var _jsxFileName$3 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/tooltip.tsx";
function TooltipProvider({ delay = 0, ...props }) {
	return /* @__PURE__ */ jsxDEV(Tooltip.Provider, {
		"data-slot": "tooltip-provider",
		delay,
		...props
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 12,
		columnNumber: 5
	}, this);
}
function Tooltip$1({ ...props }) {
	return /* @__PURE__ */ jsxDEV(TooltipProvider, { children: /* @__PURE__ */ jsxDEV(Tooltip.Root, {
		"data-slot": "tooltip",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 23,
		columnNumber: 7
	}, this) }, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 22,
		columnNumber: 5
	}, this);
}
function TooltipTrigger({ ...props }) {
	return /* @__PURE__ */ jsxDEV(Tooltip.Trigger, {
		"data-slot": "tooltip-trigger",
		...props
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 29,
		columnNumber: 10
	}, this);
}
function TooltipContent({ className, side = "top", sideOffset = 4, align = "center", alignOffset = 0, children, ...props }) {
	return /* @__PURE__ */ jsxDEV(Tooltip.Portal, { children: /* @__PURE__ */ jsxDEV(Tooltip.Positioner, {
		align,
		alignOffset,
		side,
		sideOffset,
		className: "isolate z-50",
		children: /* @__PURE__ */ jsxDEV(Tooltip.Popup, {
			"data-slot": "tooltip-content",
			className: cn("data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 rounded-md px-3 py-1.5 text-xs **:data-[slot=kbd]:rounded-md bg-foreground text-background z-50 w-fit max-w-xs origin-(--transform-origin)", className),
			...props,
			children: [children, /* @__PURE__ */ jsxDEV(Tooltip.Arrow, { className: "size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground z-50 data-[side=bottom]:top-1 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5" }, void 0, false, {
				fileName: _jsxFileName$3,
				lineNumber: 63,
				columnNumber: 11
			}, this)]
		}, void 0, true, {
			fileName: _jsxFileName$3,
			lineNumber: 54,
			columnNumber: 9
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 47,
		columnNumber: 7
	}, this) }, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 46,
		columnNumber: 5
	}, this);
}
//#endregion
//#region src/hooks/use-mobile.ts
var MOBILE_BREAKPOINT = 768;
function useIsMobile() {
	const [isMobile, setIsMobile] = React.useState(void 0);
	React.useEffect(() => {
		const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
		const onChange = () => {
			setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
		};
		mql.addEventListener("change", onChange);
		setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
		return () => mql.removeEventListener("change", onChange);
	}, []);
	return !!isMobile;
}
//#endregion
//#region src/components/ui/sidebar.tsx
var _jsxFileName$2 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/sidebar.tsx";
var SIDEBAR_COOKIE_NAME = "sidebar_state";
var SIDEBAR_COOKIE_MAX_AGE = 3600 * 24 * 7;
var SIDEBAR_WIDTH = "16rem";
var SIDEBAR_WIDTH_MOBILE = "18rem";
var SIDEBAR_WIDTH_ICON = "3rem";
var SIDEBAR_KEYBOARD_SHORTCUT = "b";
var SidebarContext = React.createContext(null);
function useSidebar() {
	const context = React.useContext(SidebarContext);
	if (!context) throw new Error("useSidebar must be used within a SidebarProvider.");
	return context;
}
function SidebarProvider({ defaultOpen = true, open: openProp, onOpenChange: setOpenProp, className, style, children, ...props }) {
	const isMobile = useIsMobile();
	const [openMobile, setOpenMobile] = React.useState(false);
	const [_open, _setOpen] = React.useState(defaultOpen);
	const open = openProp ?? _open;
	const setOpen = React.useCallback((value) => {
		const openState = typeof value === "function" ? value(open) : value;
		if (setOpenProp) setOpenProp(openState);
		else _setOpen(openState);
		document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
	}, [setOpenProp, open]);
	const toggleSidebar = React.useCallback(() => {
		return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
	}, [
		isMobile,
		setOpen,
		setOpenMobile
	]);
	React.useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
				event.preventDefault();
				toggleSidebar();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [toggleSidebar]);
	const state = open ? "expanded" : "collapsed";
	const contextValue = React.useMemo(() => ({
		state,
		open,
		setOpen,
		isMobile,
		openMobile,
		setOpenMobile,
		toggleSidebar
	}), [
		state,
		open,
		setOpen,
		isMobile,
		openMobile,
		setOpenMobile,
		toggleSidebar
	]);
	return /* @__PURE__ */ jsxDEV(SidebarContext.Provider, {
		value: contextValue,
		children: /* @__PURE__ */ jsxDEV("div", {
			"data-slot": "sidebar-wrapper",
			style: {
				"--sidebar-width": SIDEBAR_WIDTH,
				"--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
				...style
			},
			className: cn("group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full", className),
			...props,
			children
		}, void 0, false, {
			fileName: _jsxFileName$2,
			lineNumber: 129,
			columnNumber: 7
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 128,
		columnNumber: 5
	}, this);
}
function Sidebar({ side = "left", variant = "sidebar", collapsible = "offExamples", className, children, ...props }) {
	const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
	if (collapsible === "none") return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sidebar",
		className: cn("bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col", className),
		...props,
		children
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 166,
		columnNumber: 7
	}, this);
	if (isMobile) return /* @__PURE__ */ jsxDEV(Sheet, {
		open: openMobile,
		onOpenChange: setOpenMobile,
		...props,
		children: /* @__PURE__ */ jsxDEV(SheetContent, {
			"data-sidebar": "sidebar",
			"data-slot": "sidebar",
			"data-mobile": "true",
			className: "bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden",
			style: { "--sidebar-width": SIDEBAR_WIDTH_MOBILE },
			side,
			children: [/* @__PURE__ */ jsxDEV(SheetHeader, {
				className: "sr-only",
				children: [/* @__PURE__ */ jsxDEV(SheetTitle, { children: "Sidebar" }, void 0, false, {
					fileName: _jsxFileName$2,
					lineNumber: 195,
					columnNumber: 13
				}, this), /* @__PURE__ */ jsxDEV(SheetDescription, { children: "Displays the mobile sidebar." }, void 0, false, {
					fileName: _jsxFileName$2,
					lineNumber: 196,
					columnNumber: 13
				}, this)]
			}, void 0, true, {
				fileName: _jsxFileName$2,
				lineNumber: 194,
				columnNumber: 11
			}, this), /* @__PURE__ */ jsxDEV("div", {
				className: "flex h-full w-full flex-col",
				children
			}, void 0, false, {
				fileName: _jsxFileName$2,
				lineNumber: 198,
				columnNumber: 11
			}, this)]
		}, void 0, true, {
			fileName: _jsxFileName$2,
			lineNumber: 182,
			columnNumber: 9
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 181,
		columnNumber: 7
	}, this);
	return /* @__PURE__ */ jsxDEV("div", {
		className: "group peer text-sidebar-foreground hidden md:block",
		"data-state": state,
		"data-collapsible": state === "collapsed" ? collapsible : "",
		"data-variant": variant,
		"data-side": side,
		"data-slot": "sidebar",
		children: [/* @__PURE__ */ jsxDEV("div", {
			"data-slot": "sidebar-gap",
			className: cn("transition-[width] duration-200 ease-linear relative w-(--sidebar-width) bg-transparent", "group-data-[collapsible=offExamples]:w-0", "group-data-[side=right]:rotate-180", variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)")
		}, void 0, false, {
			fileName: _jsxFileName$2,
			lineNumber: 214,
			columnNumber: 7
		}, this), /* @__PURE__ */ jsxDEV("div", {
			"data-slot": "sidebar-container",
			className: cn("fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex", side === "left" ? "left-0 group-data-[collapsible=offExamples]:left-[calc(var(--sidebar-width)*-1)]" : "right-0 group-data-[collapsible=offExamples]:right-[calc(var(--sidebar-width)*-1)]", variant === "floating" || variant === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l", className),
			...props,
			children: /* @__PURE__ */ jsxDEV("div", {
				"data-sidebar": "sidebar",
				"data-slot": "sidebar-inner",
				className: "bg-sidebar group-data-[variant=floating]:ring-sidebar-border group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1 flex size-full flex-col",
				children
			}, void 0, false, {
				fileName: _jsxFileName$2,
				lineNumber: 240,
				columnNumber: 9
			}, this)
		}, void 0, false, {
			fileName: _jsxFileName$2,
			lineNumber: 225,
			columnNumber: 7
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$2,
		lineNumber: 205,
		columnNumber: 5
	}, this);
}
function SidebarTrigger({ className, onClick, ...props }) {
	const { toggleSidebar } = useSidebar();
	return /* @__PURE__ */ jsxDEV(Button, {
		"data-sidebar": "trigger",
		"data-slot": "sidebar-trigger",
		variant: "ghost",
		size: "icon-sm",
		className: cn(className),
		onClick: (event) => {
			onClick?.(event);
			toggleSidebar();
		},
		...props,
		children: [/* @__PURE__ */ jsxDEV(PanelLeftIcon, {}, void 0, false, {
			fileName: _jsxFileName$2,
			lineNumber: 272,
			columnNumber: 7
		}, this), /* @__PURE__ */ jsxDEV("span", {
			className: "sr-only",
			children: "Toggle Sidebar"
		}, void 0, false, {
			fileName: _jsxFileName$2,
			lineNumber: 274,
			columnNumber: 7
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$2,
		lineNumber: 260,
		columnNumber: 5
	}, this);
}
function SidebarRail({ className, ...props }) {
	const { toggleSidebar } = useSidebar();
	return /* @__PURE__ */ jsxDEV("button", {
		"data-sidebar": "rail",
		"data-slot": "sidebar-rail",
		"aria-label": "Toggle Sidebar",
		tabIndex: -1,
		onClick: toggleSidebar,
		title: "Toggle Sidebar",
		className: cn("hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex", "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize", "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize", "hover:group-data-[collapsible=offExamples]:bg-sidebar group-data-[collapsible=offExamples]:translate-x-0 group-data-[collapsible=offExamples]:after:left-full", "[[data-side=left][data-collapsible=offExamples]_&]:-right-2", "[[data-side=right][data-collapsible=offExamples]_&]:-left-2", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 283,
		columnNumber: 5
	}, this);
}
function SidebarInset({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("main", {
		"data-slot": "sidebar-inset",
		className: cn("bg-background md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 relative flex w-full flex-1 flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 306,
		columnNumber: 5
	}, this);
}
function SidebarInput({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Input, {
		"data-slot": "sidebar-input",
		"data-sidebar": "input",
		className: cn("bg-muted/20 dark:bg-muted/30 border-input h-8 w-full", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 322,
		columnNumber: 5
	}, this);
}
function SidebarHeader({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sidebar-header",
		"data-sidebar": "header",
		className: cn("gap-2 p-2 flex flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 333,
		columnNumber: 5
	}, this);
}
function SidebarFooter({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sidebar-footer",
		"data-sidebar": "footer",
		className: cn("gap-2 p-2 flex flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 344,
		columnNumber: 5
	}, this);
}
function SidebarSeparator({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV(Separator, {
		"data-slot": "sidebar-separator",
		"data-sidebar": "separator",
		className: cn("bg-sidebar-border mx-2 w-auto", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 358,
		columnNumber: 5
	}, this);
}
function SidebarContent({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sidebar-content",
		"data-sidebar": "content",
		className: cn("no-scrollbar gap-0 flex min-h-0 flex-1 flex-col overflow-auto group-data-[collapsible=icon]:overflow-hidden", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 369,
		columnNumber: 5
	}, this);
}
function SidebarGroup({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sidebar-group",
		"data-sidebar": "group",
		className: cn("px-2 py-1 relative flex w-full min-w-0 flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 383,
		columnNumber: 5
	}, this);
}
function SidebarGroupLabel({ className, render, ...props }) {
	return useRender({
		defaultTagName: "div",
		props: mergeProps({ className: cn("text-sidebar-foreground/70 ring-sidebar-ring h-8 rounded-md px-2 text-xs transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 flex shrink-0 items-center outline-hidden [&>svg]:shrink-0", className) }, props),
		render,
		state: {
			slot: "sidebar-group-label",
			sidebar: "group-label"
		}
	});
}
function SidebarGroupAction({ className, render, ...props }) {
	return useRender({
		defaultTagName: "button",
		props: mergeProps({ className: cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 w-5 rounded-md p-0 focus-visible:ring-2 [&>svg]:size-4 flex aspect-square items-center justify-center outline-hidden transition-transform [&>svg]:shrink-0 after:absolute after:-inset-2 md:after:hidden group-data-[collapsible=icon]:hidden", className) }, props),
		render,
		state: {
			slot: "sidebar-group-action",
			sidebar: "group-action"
		}
	});
}
function SidebarGroupContent({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sidebar-group-content",
		"data-sidebar": "group-content",
		className: cn("text-xs w-full", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 448,
		columnNumber: 5
	}, this);
}
function SidebarMenu({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("ul", {
		"data-slot": "sidebar-menu",
		"data-sidebar": "menu",
		className: cn("gap-px flex w-full min-w-0 flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 459,
		columnNumber: 5
	}, this);
}
function SidebarMenuItem({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("li", {
		"data-slot": "sidebar-menu-item",
		"data-sidebar": "menu-item",
		className: cn("group/menu-item relative", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 470,
		columnNumber: 5
	}, this);
}
var sidebarMenuButtonVariants = cva("ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground gap-2 rounded-[calc(var(--radius-sm)+2px)] p-2 text-left text-xs transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! focus-visible:ring-2 data-active:font-medium peer/menu-button flex w-full items-center overflow-hidden outline-hidden group/menu-button disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&_svg]:size-4 [&_svg]:shrink-0", {
	variants: {
		variant: {
			default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
			outline: "bg-background hover:bg-sidebar-accent hover:text-sidebar-accent-foreground shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]"
		},
		size: {
			default: "h-8 text-xs",
			sm: "h-7 text-xs",
			lg: "h-12 text-xs group-data-[collapsible=icon]:p-0!"
		}
	},
	defaultVariants: {
		variant: "default",
		size: "default"
	}
});
function SidebarMenuButton({ render, isActive = false, variant = "default", size = "default", tooltip, className, ...props }) {
	const { isMobile, state } = useSidebar();
	const comp = useRender({
		defaultTagName: "button",
		props: mergeProps({ className: cn(sidebarMenuButtonVariants({
			variant,
			size
		}), className) }, props),
		render: !tooltip ? render : TooltipTrigger,
		state: {
			slot: "sidebar-menu-button",
			sidebar: "menu-button",
			size,
			active: isActive
		}
	});
	if (!tooltip) return comp;
	if (typeof tooltip === "string") tooltip = { children: tooltip };
	return /* @__PURE__ */ jsxDEV(Tooltip$1, { children: [comp, /* @__PURE__ */ jsxDEV(TooltipContent, {
		side: "right",
		align: "center",
		hidden: state !== "collapsed" || isMobile,
		...tooltip
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 544,
		columnNumber: 7
	}, this)] }, void 0, true, {
		fileName: _jsxFileName$2,
		lineNumber: 542,
		columnNumber: 5
	}, this);
}
function SidebarMenuAction({ className, render, showOnHover = false, ...props }) {
	return useRender({
		defaultTagName: "button",
		props: mergeProps({ className: cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 aspect-square w-5 rounded-[calc(var(--radius-sm)-2px)] p-0 peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 focus-visible:ring-2 [&>svg]:size-4 flex items-center justify-center outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 md:after:hidden [&>svg]:shrink-0", showOnHover && "peer-data-active/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-open:opacity-100 md:opacity-0", className) }, props),
		render,
		state: {
			slot: "sidebar-menu-action",
			sidebar: "menu-action"
		}
	});
}
function SidebarMenuBadge({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sidebar-menu-badge",
		"data-sidebar": "menu-badge",
		className: cn("text-sidebar-foreground peer-hover/menu-button:text-sidebar-accent-foreground peer-data-active/menu-button:text-sidebar-accent-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 rounded-[calc(var(--radius-sm)-2px)] px-1 text-xs font-medium peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 flex items-center justify-center tabular-nums select-none group-data-[collapsible=icon]:hidden", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 589,
		columnNumber: 5
	}, this);
}
function SidebarMenuSkeleton({ className, showIcon = false, ...props }) {
	const [width] = React.useState(() => {
		return `${Math.floor(Math.random() * 40) + 50}%`;
	});
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "sidebar-menu-skeleton",
		"data-sidebar": "menu-skeleton",
		className: cn("h-8 gap-2 rounded-md px-2 flex items-center", className),
		...props,
		children: [showIcon && /* @__PURE__ */ jsxDEV(Skeleton, {
			className: "size-4 rounded-md",
			"data-sidebar": "menu-skeleton-icon"
		}, void 0, false, {
			fileName: _jsxFileName$2,
			lineNumber: 621,
			columnNumber: 9
		}, this), /* @__PURE__ */ jsxDEV(Skeleton, {
			className: "h-4 max-w-(--skeleton-width) flex-1",
			"data-sidebar": "menu-skeleton-text",
			style: { "--skeleton-width": width }
		}, void 0, false, {
			fileName: _jsxFileName$2,
			lineNumber: 626,
			columnNumber: 7
		}, this)]
	}, void 0, true, {
		fileName: _jsxFileName$2,
		lineNumber: 614,
		columnNumber: 5
	}, this);
}
function SidebarMenuSub({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("ul", {
		"data-slot": "sidebar-menu-sub",
		"data-sidebar": "menu-sub",
		className: cn("border-sidebar-border mx-3.5 translate-x-px gap-1 border-l px-2.5 py-0.5 group-data-[collapsible=icon]:hidden flex min-w-0 flex-col", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 641,
		columnNumber: 5
	}, this);
}
function SidebarMenuSubItem({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("li", {
		"data-slot": "sidebar-menu-sub-item",
		"data-sidebar": "menu-sub-item",
		className: cn("group/menu-sub-item relative", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 655,
		columnNumber: 5
	}, this);
}
function SidebarMenuSubButton({ render, size = "md", isActive = false, className, ...props }) {
	return useRender({
		defaultTagName: "a",
		props: mergeProps({ className: cn("text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground h-7 gap-2 rounded-md px-2 focus-visible:ring-2 data-[size=md]:text-xs data-[size=sm]:text-xs [&>svg]:size-4 flex min-w-0 -translate-x-px items-center overflow-hidden outline-hidden group-data-[collapsible=icon]:hidden disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:shrink-0", className) }, props),
		render,
		state: {
			slot: "sidebar-menu-sub-button",
			sidebar: "menu-sub-button",
			size,
			active: isActive
		}
	});
}
//#endregion
//#region src/components/ui/sonner.tsx
var _jsxFileName$1 = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/sonner.tsx";
var Toaster = ({ ...props }) => {
	const { theme = "system" } = useTheme();
	return /* @__PURE__ */ jsxDEV(Toaster$1, {
		theme,
		className: "toaster group",
		icons: {
			success: /* @__PURE__ */ jsxDEV(CircleCheckIcon, { className: "size-4" }, void 0, false, {
				fileName: _jsxFileName$1,
				lineNumber: 14,
				columnNumber: 11
			}, void 0),
			info: /* @__PURE__ */ jsxDEV(InfoIcon, { className: "size-4" }, void 0, false, {
				fileName: _jsxFileName$1,
				lineNumber: 17,
				columnNumber: 11
			}, void 0),
			warning: /* @__PURE__ */ jsxDEV(TriangleAlertIcon, { className: "size-4" }, void 0, false, {
				fileName: _jsxFileName$1,
				lineNumber: 20,
				columnNumber: 11
			}, void 0),
			error: /* @__PURE__ */ jsxDEV(OctagonXIcon, { className: "size-4" }, void 0, false, {
				fileName: _jsxFileName$1,
				lineNumber: 23,
				columnNumber: 11
			}, void 0),
			loading: /* @__PURE__ */ jsxDEV(Loader2Icon, { className: "size-4 animate-spin" }, void 0, false, {
				fileName: _jsxFileName$1,
				lineNumber: 26,
				columnNumber: 11
			}, void 0)
		},
		style: {
			"--normal-bg": "var(--popover)",
			"--normal-text": "var(--popover-foreground)",
			"--normal-border": "var(--border)",
			"--border-radius": "var(--radius)"
		},
		toastOptions: { classNames: { toast: "cn-toast" } },
		...props
	}, void 0, false, {
		fileName: _jsxFileName$1,
		lineNumber: 9,
		columnNumber: 5
	}, void 0);
};
//#endregion
//#region src/components/ui/table.tsx
var _jsxFileName = "/home/runner/work/chip-monorepo/chip-monorepo/packages/lib-web-ui-shadcn/src/components/ui/table.tsx";
function Table({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("div", {
		"data-slot": "table-container",
		className: "relative w-full overflow-x-auto",
		children: /* @__PURE__ */ jsxDEV("table", {
			"data-slot": "table",
			className: cn("w-full caption-bottom text-xs", className),
			...props
		}, void 0, false, {
			fileName: _jsxFileName,
			lineNumber: 8,
			columnNumber: 7
		}, this)
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 7,
		columnNumber: 5
	}, this);
}
function TableHeader({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("thead", {
		"data-slot": "table-header",
		className: cn("[&_tr]:border-b", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 19,
		columnNumber: 5
	}, this);
}
function TableBody({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("tbody", {
		"data-slot": "table-body",
		className: cn("[&_tr:last-child]:border-0", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 29,
		columnNumber: 5
	}, this);
}
function TableFooter({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("tfoot", {
		"data-slot": "table-footer",
		className: cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 39,
		columnNumber: 5
	}, this);
}
function TableRow({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("tr", {
		"data-slot": "table-row",
		className: cn("hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 49,
		columnNumber: 5
	}, this);
}
function TableHead({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("th", {
		"data-slot": "table-head",
		className: cn("text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 59,
		columnNumber: 5
	}, this);
}
function TableCell({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("td", {
		"data-slot": "table-cell",
		className: cn("p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 69,
		columnNumber: 5
	}, this);
}
function TableCaption({ className, ...props }) {
	return /* @__PURE__ */ jsxDEV("caption", {
		"data-slot": "table-caption",
		className: cn("text-muted-foreground mt-4 text-xs", className),
		...props
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 82,
		columnNumber: 5
	}, this);
}
//#endregion
export { Alert, AlertAction, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogMedia, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AlertTitle, Avatar, AvatarBadge, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage, Badge, Button, ButtonGroup, ButtonGroupSeparator, ButtonGroupText, Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Combobox, ComboboxChip, ComboboxChips, ComboboxChipsInput, ComboboxCollection, ComboboxContent, ComboboxEmpty, ComboboxGroup, ComboboxInput, ComboboxItem, ComboboxLabel, ComboboxList, ComboboxSeparator, ComboboxTrigger, ComboboxValue, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, Field, FieldContent, FieldDescription, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSeparator, FieldSet, FieldTitle, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, Input, InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText, InputGroupTextarea, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, Label, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator, Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger, Skeleton, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, Textarea, Toaster, badgeVariants, buttonGroupVariants, buttonVariants, toast, useComboboxAnchor, useFormField, useSidebar };

//# sourceMappingURL=index.js.map